import { Appearance } from "@stripe/stripe-js";
import {ShippingRegion} from "../types/types";

type SupportedCountryCode = "NZ" | "AU" | "GB" | "US" | "CA";

interface CountryShippingConfig {
  defaultCity: string;
  defaultProvince: string;
  regions: ShippingRegion[];
}

interface PaymentEnabledConfig {
  stripe: boolean;
  afterpay: boolean;
  alipay: boolean;
  paypal: boolean;
  farmlands: boolean;
  gem: boolean;
  poli: boolean;
}

interface IConfig {
  activeClub: string;
  countryCode: string;
  googleMapsCountryCode: string;
  countryLabel: string;
  currencyCode: string;
  environment: string;
  appUrl: string;
  checkoutUrl: string;
  storeUrl: string;
  stripeKey: string;
  stripeSecret: string;
  windcaveUrl: string;
  windcaveSecret: string;
  windcaveWebhookUrl: string;
  gemUrl: string;
  gemMid: string;
  gemSecret: string;
  paypalUrl: string;
  paypalClientId: string;
  paypalClientSecret: string;
  poliUrl: string;
  poliMerchantCode: string;
  poliSecret: string;
  poliWebhookUrl: string;
  stripeWebhookSecret: string;
  shopifyAccessToken: string;
  shopifyAppPassword: string;
  shopifyAdminUrl: string;
  shopifyStorefrontUrl: string;
  defaultShippingCity: string;
  defaultShippingProvince: string;
  shippingRegions: ShippingRegion[];
  paymentEnabled: PaymentEnabledConfig;
  giftCardsEnabled: boolean;
  supabaseUrl: string;
  supabaseKey: string;
  sentryDsn: string;
  mixpanelToken: string;
  stripeTheme: Appearance;
  fonts: {
    family: string;
    src: string;
    weight: string;
  }[];
  hyperDx: {
    apiKey: string;
    betaMode: boolean;
  },
  hyperDxAttributes: Record<string,string>;
  partnerCodes: string[];
  limitedUseDiscountCodes: string[];
  validateDiscountCodesOnPayment: boolean;
  dashboardCheckoutMode: "disabled" | "shadow";
  dashboardApiUrl: string;
  dashboardShadowIntakeSecret: string;
  dashboardShadowCorrelationAttribute: string;
}

const shippingConfig: Record<SupportedCountryCode, CountryShippingConfig> = {
  NZ: {
    defaultCity: "Auckland",
    defaultProvince: "AUK",
    regions: [
      { code: "AUK", label: "Auckland" },
      { code: "WGN", label: "Wellington" },
      { code: "WKO", label: "Waikato" },
      { code: "NTL", label: "Northland" },
      { code: "BOP", label: "Bay of Plenty" },
      { code: "HKB", label: "Hawke's Bay" },
      { code: "GIS", label: "Gisborne" },
      { code: "MWT", label: "Manawatu-Wanganui" },
      { code: "TKI", label: "Taranaki" },
      { code: "TAS", label: "Tasman" },
      { code: "NSN", label: "Nelson" },
      { code: "MBH", label: "Marlborough" },
      { code: "CAN", label: "Canterbury" },
      { code: "WTC", label: "West Coast" },
      { code: "OTA", label: "Otago" },
      { code: "STL", label: "Southland" },
    ],
  },
  AU: {
    defaultCity: "Sydney",
    defaultProvince: "NSW",
    regions: [
      { code: "NSW", label: "New South Wales" },
      { code: "VIC", label: "Victoria" },
      { code: "QLD", label: "Queensland" },
      { code: "WA", label: "Western Australia" },
      { code: "SA", label: "South Australia" },
      { code: "TAS", label: "Tasmania" },
      { code: "ACT", label: "Australian Capital Territory" },
      { code: "NT", label: "Northern Territory" },
    ],
  },
  GB: {
    defaultCity: "London",
    defaultProvince: "ENG",
    regions: [
      { code: "ENG", label: "England" },
      { code: "SCT", label: "Scotland" },
      { code: "WLS", label: "Wales" },
      { code: "NIR", label: "Northern Ireland" },
    ],
  },
  US: {
    defaultCity: "Los Angeles",
    defaultProvince: "CA",
    regions: [
      { code: "AL", label: "Alabama" },
      { code: "AK", label: "Alaska" },
      { code: "AZ", label: "Arizona" },
      { code: "AR", label: "Arkansas" },
      { code: "CA", label: "California" },
      { code: "CO", label: "Colorado" },
      { code: "CT", label: "Connecticut" },
      { code: "DE", label: "Delaware" },
      { code: "FL", label: "Florida" },
      { code: "GA", label: "Georgia" },
      { code: "HI", label: "Hawaii" },
      { code: "ID", label: "Idaho" },
      { code: "IL", label: "Illinois" },
      { code: "IN", label: "Indiana" },
      { code: "IA", label: "Iowa" },
      { code: "KS", label: "Kansas" },
      { code: "KY", label: "Kentucky" },
      { code: "LA", label: "Louisiana" },
      { code: "ME", label: "Maine" },
      { code: "MD", label: "Maryland" },
      { code: "MA", label: "Massachusetts" },
      { code: "MI", label: "Michigan" },
      { code: "MN", label: "Minnesota" },
      { code: "MS", label: "Mississippi" },
      { code: "MO", label: "Missouri" },
      { code: "MT", label: "Montana" },
      { code: "NE", label: "Nebraska" },
      { code: "NV", label: "Nevada" },
      { code: "NH", label: "New Hampshire" },
      { code: "NJ", label: "New Jersey" },
      { code: "NM", label: "New Mexico" },
      { code: "NY", label: "New York" },
      { code: "NC", label: "North Carolina" },
      { code: "ND", label: "North Dakota" },
      { code: "OH", label: "Ohio" },
      { code: "OK", label: "Oklahoma" },
      { code: "OR", label: "Oregon" },
      { code: "PA", label: "Pennsylvania" },
      { code: "RI", label: "Rhode Island" },
      { code: "SC", label: "South Carolina" },
      { code: "SD", label: "South Dakota" },
      { code: "TN", label: "Tennessee" },
      { code: "TX", label: "Texas" },
      { code: "UT", label: "Utah" },
      { code: "VT", label: "Vermont" },
      { code: "VA", label: "Virginia" },
      { code: "WA", label: "Washington" },
      { code: "WV", label: "West Virginia" },
      { code: "WI", label: "Wisconsin" },
      { code: "WY", label: "Wyoming" },
    ],
  },
  // Canada (2026-08-14): market activated in Shopify 2026-08-13 (CAD).
  // The canonical market resolver (market.ts MARKET_DEFINITIONS) already
  // carries CA (suffix/hiddenCA/club_ca — P1 amendment §8b) and the Stripe
  // currency map already accepts CAD; this entry is the last piece that
  // lets a deployment boot with REACT_APP_CHECKOUT_COUNTRY=CA instead of
  // tripping requireSupportedCountryCode's refuse-to-boot guard.
  CA: {
    defaultCity: "Toronto",
    defaultProvince: "ON",
    regions: [
      { code: "ON", label: "Ontario" },
      { code: "QC", label: "Quebec" },
      { code: "BC", label: "British Columbia" },
      { code: "AB", label: "Alberta" },
      { code: "MB", label: "Manitoba" },
      { code: "SK", label: "Saskatchewan" },
      { code: "NS", label: "Nova Scotia" },
      { code: "NB", label: "New Brunswick" },
      { code: "NL", label: "Newfoundland and Labrador" },
      { code: "PE", label: "Prince Edward Island" },
      { code: "NT", label: "Northwest Territories" },
      { code: "YT", label: "Yukon" },
      { code: "NU", label: "Nunavut" },
    ],
  },
};

function isSupportedCountryCode(countryCode: string): countryCode is SupportedCountryCode {
  return countryCode in shippingConfig;
}

function envFlag(name: string, defaultValue: boolean) {
  const value = process.env[name];
  if (value === undefined) {
    return defaultValue;
  }
  return /^true$/i.test(value);
}

function dashboardCheckoutMode(): "disabled" | "shadow" {
  const mode = (process.env.DASHBOARD_CHECKOUT_MODE ?? "disabled").toLowerCase();
  if (mode === "shadow") {
    return mode;
  }
  if (mode === "primary") {
    throw new Error(
      "DASHBOARD_CHECKOUT_MODE=primary is unavailable until the Dashboard payment-session migration is complete",
    );
  }
  return "disabled";
}

function defaultActiveClub(countryCode: string) {
  if (countryCode === "AU") {
    return "club_au";
  }
  if (countryCode === "GB") {
    return "club_uk";
  }
  if (countryCode === "US") {
    return "club_us";
  }
  return "club_nz";
}

function defaultShippingProvince(countryShippingConfig: CountryShippingConfig) {
  const configuredProvince = process.env.REACT_APP_DEFAULT_SHIPPING_PROVINCE;
  if (!configuredProvince) {
    return countryShippingConfig.defaultProvince;
  }

  const configuredRegion = countryShippingConfig.regions.find(
    (region) => region.code === configuredProvince,
  );

  return configuredRegion?.code ?? countryShippingConfig.defaultProvince;
}

const configuredCountryCode = process.env.REACT_APP_CHECKOUT_COUNTRY ?? "NZ";
const countryCode = isSupportedCountryCode(configuredCountryCode) ? configuredCountryCode : "NZ";
const countryShippingConfig = shippingConfig[countryCode];

const config: IConfig = {
  activeClub: process.env.REACT_APP_ACTIVE_CLUB ?? defaultActiveClub(countryCode),
  countryLabel: process.env.REACT_APP_CHECKOUT_COUNTRY_LABEL ?? "New Zealand",
  countryCode,
  googleMapsCountryCode:
    process.env.REACT_APP_MAPS_COUNTRY ?? (countryCode === "GB" ? "uk" : countryCode.toLowerCase()),
  currencyCode: process.env.REACT_APP_CHECKOUT_CURRENCY ?? "NZD",
  environment: process.env.REACT_APP_CHECKOUT_ENV ?? "production",
  appUrl: process.env.REACT_APP_URL ?? "",
  checkoutUrl: process.env.REACT_APP_CHECKOUT_URL ?? "",
  storeUrl: process.env.REACT_APP_STORE_URL ?? "",
  stripeKey: process.env.REACT_APP_STRIPE_KEY ?? "",
  stripeSecret: process.env.STRIPE_SECRET_KEY ?? "",
  stripeWebhookSecret: process.env.STRIPE_WEBHOOK_SECRET ?? "",
  windcaveUrl: process.env.WINDCAVE_URL ?? "",
  windcaveSecret: process.env.WINDCAVE_SECRET ?? "",
  windcaveWebhookUrl: process.env.WINDCAVE_WEBHOOK_URL ?? "",
  gemUrl: process.env.GEM_URL ?? "",
  gemMid: process.env.GEM_MID ?? "",
  gemSecret: process.env.GEM_SECRET ?? "",
  paypalUrl: process.env.PAYPAL_URL ?? "",
  paypalClientId: process.env.REACT_APP_PAYPAL_CLIENT_ID ?? "",
  paypalClientSecret: process.env.PAYPAL_CLIENT_SECRET ?? "",
  poliUrl: process.env.POLI_URL ?? "",
  poliMerchantCode: process.env.POLI_MERCHANT_CODE ?? "",
  poliSecret: process.env.POLI_SECRET ?? "",
  poliWebhookUrl: process.env.POLI_WEBHOOK_URL ?? "",
  shopifyAccessToken: process.env.REACT_APP_SHOPIFY_ACCESS_TOKEN ?? "",
  shopifyAppPassword: process.env.SHOPIFY_APP_PASSWORD ?? "",
  shopifyAdminUrl: process.env.SHOPIFY_ADMIN_URL ?? "",
  shopifyStorefrontUrl: process.env.REACT_APP_SHOPIFY_STOREFRONT_URL ?? "",
  defaultShippingCity:
    process.env.REACT_APP_DEFAULT_SHIPPING_CITY ?? countryShippingConfig.defaultCity,
  defaultShippingProvince: defaultShippingProvince(countryShippingConfig),
  shippingRegions: countryShippingConfig.regions,
  paymentEnabled: {
    stripe: envFlag("REACT_APP_STRIPE_ENABLED", true),
    afterpay: envFlag("REACT_APP_AFTERPAY_ENABLED", true),
    alipay: envFlag("REACT_APP_ALIPAY_ENABLED", true),
    paypal: envFlag("REACT_APP_PAYPAL_ENABLED", true),
    farmlands: envFlag("REACT_APP_FARMLANDS_ENABLED", true),
    gem: envFlag("REACT_APP_GEM_ENABLED", true),
    poli: envFlag("REACT_APP_POLI_ENABLED", true),
  },
  giftCardsEnabled: envFlag("REACT_APP_GIFT_CARDS_ENABLED", true),
  supabaseUrl: process.env.SUPABASE_URL ?? "",
  supabaseKey: process.env.SUPABASE_KEY ?? "",
  sentryDsn: process.env.REACT_APP_SENTRY_DNS ?? "",
  mixpanelToken: process.env.REACT_APP_MIXPANEL_TOKEN ?? "",
  stripeTheme: {
    theme: "stripe",
    variables: {
      fontFamily: '"Axiforma", Arial, sans-serif',
      fontLineHeight: "1.5",
      colorPrimary: "#30313d",
      colorPrimaryText: "#fff",
      colorBackgroundText: "#fff",
      borderRadius: "1.5rem",
    },
    rules: {
      ".LinkButton": {
        borderRadius: "9999px",
      },
    },
  },
  fonts: [
    {
      family: "Axiforma",
      src: "url(/fonts/axiforma/axiforma-bold.woff2)",
      weight: "700",
    },
    {
      family: "Axiforma",
      src: "url(/fonts/axiforma/axiforma-extrabold.woff2)",
      weight: "800",
    },
    {
      family: "Axiforma",
      src: "url(/fonts/axiforma/axiforma-light.woff2)",
      weight: "300",
    },
  ],
  hyperDx: {
    apiKey: process.env.REACT_APP_HYPERDX_API_KEY ?? '',
    betaMode: true,
  },
  hyperDxAttributes: {
    environment: process.env.REACT_APP_CHECKOUT_ENV ?? "production",
    countryCode,
  },
  partnerCodes: ['GROOTER', 'TEAMGOOSEDOWN', 'PISTOLPETE', 'BANDR', 'LINCOLNHC', 'RWXBB'],
  limitedUseDiscountCodes: [
    'MERINO', 'MILFORD', 'HEADLAMP', 'CARBON', 'APEX', 'TONGARIRO', 
    'CAMPFIRE', 'SUMMIT', 'KIMBERLEY', 'ULURU', 'HYDRA', 'WATERPROOF', 
    'AORAKI',  'ROUTEBURN', 'RIDGE', 'ROAMER', 'PILBARA', 'GLACIER'],
  validateDiscountCodesOnPayment: /^true$/i.test(
    process.env.REACT_APP_RESERVE_DISCOUNT_CODES ?? "",
  ),
  // This is deliberately a server-only setting. Shadow mode mirrors an order
  // attempt to the Dashboard and still creates the Shopify order locally.
  dashboardCheckoutMode: dashboardCheckoutMode(),
  dashboardApiUrl: process.env.DASHBOARD_API_URL ?? "",
  dashboardShadowIntakeSecret: process.env.DASHBOARD_SHADOW_INTAKE_SECRET ?? "",
  dashboardShadowCorrelationAttribute:
    process.env.DASHBOARD_SHADOW_CORRELATION_ATTRIBUTE ?? "checkout_shadow_id",
};
export default config;
